/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import NotificationView, {
  NotificationViewStyle,
} from '@airasia-phoenix/react-native/components/NotificationView';
import PINView from '@airasia-phoenix/react-native/components/PINView';
import TextView, {
  SpannableTextProps,
} from '@airasia-phoenix/react-native/components/TextView';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  InteractionManager,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import styles from './styles';

export interface OTPViewRef {
  startTimer: () => void;
}

const INLINE_ERROR_CODE = 'OTP_VERIFICATION_FAIL';

export interface OTPViewProps {
  title?: string;
  subtitle?: string;
  subtitleSpannableTextsProps?: SpannableTextProps[];
  showTitle?: boolean;
  errorMessage?: string;
  errorCode?: string;
  onOTPSubmit?: (otp: string) => void;
  onRequestNewOTP?: () => void;
  onErrorClear?: () => void;
  testID?: string;
  errorTestID?: string;
  timerSeconds?: number;
  timerHeaderText?: string;
  getTimerText: (timer: number) => string;
  getTimerSpannableTextsProps?: (timer: number) => SpannableTextProps[];
  autoSubmit?: boolean;
  renderFooter?: React.ReactNode | ((timer: number) => React.ReactNode);
  pinInputFieldTextStyle?: Record<string, unknown>;
  timerResendRowStyle?: StyleProp<ViewStyle>;
  deferPinMount?: boolean;
}

const OTPView = React.forwardRef<OTPViewRef, OTPViewProps>(function OTPView(
  {
    title,
    subtitle,
    subtitleSpannableTextsProps,
    showTitle = true,
    errorMessage,
    errorCode,
    onOTPSubmit,
    onRequestNewOTP,
    onErrorClear,
    testID,
    errorTestID,
    timerSeconds = 60,
    timerHeaderText,
    getTimerText,
    getTimerSpannableTextsProps,
    autoSubmit = true,
    renderFooter,
    pinInputFieldTextStyle,
    timerResendRowStyle,
    deferPinMount = false,
  }: OTPViewProps,
  ref,
) {
  const [pin, setPIN] = useState('');
  const [timer, setTimer] = useState(timerSeconds);
  const [timerRunning, setTimerRunning] = useState(false);
  const [pinResetKey, setPinResetKey] = useState(0);
  const [isPinMounted, setIsPinMounted] = useState(!deferPinMount);
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (!deferPinMount) return;
    const task = InteractionManager.runAfterInteractions(() =>
      setIsPinMounted(true),
    );
    return () => task.cancel();
  }, [deferPinMount]);

  useImperativeHandle(ref, () => ({
    startTimer: () => {
      setTimer(timerSeconds);
      setTimerRunning(true);
    },
  }));

  useEffect(() => {
    if (!timerRunning || timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          setTimerRunning(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerRunning, timer]);

  useEffect(() => {
    if (!autoSubmit || !onOTPSubmit) return;
    if (pin.length === 6 && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      onOTPSubmit(pin);
    }
  }, [autoSubmit, onOTPSubmit, pin]);

  useEffect(() => {
    if (pin.length < 6) {
      hasSubmittedRef.current = false;
    }
  }, [pin]);

  const handleRequestNewOTP = () => {
    if (timer === 0) {
      setPIN('');
      setPinResetKey((k) => k + 1);
      onRequestNewOTP?.();
    }
  };

  const handlePinChange = (pinValue: string[]) => {
    setPIN(pinValue.join(''));
    if (errorMessage && onErrorClear) {
      onErrorClear();
    }
  };

  /** Set only when the error belongs under the fields rather than above them. */
  const inlineError =
    errorCode === INLINE_ERROR_CODE ? errorMessage : undefined;
  const footer =
    typeof renderFooter === 'function' ? renderFooter(timer) : renderFooter;

  return (
    <View style={styles.mainContainer} testID={testID}>
      {errorMessage && !inlineError && (
        <NotificationView
          width={'100%'}
          text={errorMessage}
          style={NotificationViewStyle.WARNING}
          testID={
            errorTestID ?? (testID ? `${testID}-ErrorNotification` : undefined)
          }
        />
      )}

      {showTitle && title ? (
        <TextView text={title} style={styles.title} />
      ) : null}
      {subtitle ? (
        <TextView
          text={subtitle}
          style={styles.subtitle}
          spannableTextsProps={subtitleSpannableTextsProps}
        />
      ) : null}

      <View style={styles.pinContainer}>
        {isPinMounted ? (
          <PINView
            key={pinResetKey}
            numberOfPINFields={6}
            onChange={handlePinChange}
            inputFieldStyle={
              inlineError ? { ...styles.pinFieldError } : styles.pinField
            }
            inputFieldTextStyle={
              inlineError
                ? { ...pinInputFieldTextStyle, ...styles.pinTextError }
                : pinInputFieldTextStyle
            }
          />
        ) : null}
        {inlineError && (
          <TextView text={inlineError} style={styles.inlineError} />
        )}
      </View>

      {timerHeaderText && (
        <>
          <TextView text={timerHeaderText} style={styles.timerText} />
          <TouchableOpacity
            style={timerResendRowStyle}
            onPress={handleRequestNewOTP}
            disabled={timer > 0}
            testID={
              testID ? `${testID}-resend-button` : 'OTPView-resend-button'
            }
          >
            <TextView
              text={getTimerText(timer) ?? ''}
              style={styles.timerText}
              spannableTextsProps={
                getTimerSpannableTextsProps?.(timer) ?? undefined
              }
            />
          </TouchableOpacity>
        </>
      )}

      {footer}
    </View>
  );
});

export default OTPView;
